"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Label } from "@/components/ui/form/Label";
import { Button } from "@/components/ui/buttons";
import { Icon } from "@/components/ui/Icon";
const dropzoneVariants = cva("relative w-full rounded-xl border-2 px-md py-lg transition-all duration-200 has-disabled:opacity-50 focus-within:outline-2", {
  variants: {
    color: {
      purple: "border-purple text-black focus-within:outline-purple",
      yellow: "border-yellow text-black focus-within:outline-yellow",
      dark: "border-white/60 bg-black/40 backdrop-blur-md text-white focus-within:outline-yellow"
    },
    state: {
      /** Nothing picked yet — the dashed border reads as "drop here". */
      empty: "border-dashed",
      /** A file is attached: solid border, filename + type. */
      filled: "border-solid",
      /** Rejected file: solid red, never dashed. */
      error: "border-solid border-red-500!"
    }
  },
  defaultVariants: {
    color: "purple",
    state: "empty"
  }
});
export interface FileuploadProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "color" | "value" | "type">, Omit<VariantProps<typeof dropzoneVariants>, "state"> {
  /** Error message shown inside the box; also switches it to the red state */
  error?: string;
  /** Label for the control */
  label?: string;
  /** Currently selected file, if any */
  file?: File | null;
  /** "Drag a file here to add it" */
  dragLabel?: string;
  /** "Maximum file size …" */
  maxSizeLabel?: string;
  /** Text on the primary button */
  chooseLabel?: string;
  /** Accessible name for the button that clears the selection */
  clearLabel?: string;
}

/** "einreichung.pdf" → "PDF". Extension-based, matching how uploads are validated. */
const fileTypeLabel = (file: File) => file.name.includes(".") ? (file.name.split(".").pop() ?? "").toUpperCase() : file.type;

/**
 * Dropzone for HubSpot `file` fields.
 *
 * The real `<input type="file">` is laid transparently over the whole box
 * rather than hidden. That buys three things at once: the box is clickable
 * everywhere, files dropped on it are handled natively (no drag-and-drop
 * JavaScript at all), and `required` stays reportable — a visually hidden
 * input makes `reportValidity()` fail on an unfocusable element and blocks
 * submit with nothing but a console error. The primary button is therefore
 * `isDummy` (a span): the input above it is the real control, so there is one
 * focusable element here, not two.
 */
export const Fileupload: React.FC<FileuploadProps> = t0 => {
  const $ = _c(49);
  let chooseLabel;
  let className;
  let clearLabel;
  let dragLabel;
  let error;
  let file;
  let id;
  let label;
  let maxSizeLabel;
  let onChange;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      color: t1,
      error,
      label,
      className,
      id,
      file,
      dragLabel,
      maxSizeLabel,
      chooseLabel,
      clearLabel,
      onChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = chooseLabel;
    $[2] = className;
    $[3] = clearLabel;
    $[4] = dragLabel;
    $[5] = error;
    $[6] = file;
    $[7] = id;
    $[8] = label;
    $[9] = maxSizeLabel;
    $[10] = onChange;
    $[11] = props;
    $[12] = t1;
  } else {
    chooseLabel = $[1];
    className = $[2];
    clearLabel = $[3];
    dragLabel = $[4];
    error = $[5];
    file = $[6];
    id = $[7];
    label = $[8];
    maxSizeLabel = $[9];
    onChange = $[10];
    props = $[11];
    t1 = $[12];
  }
  const color = t1 === undefined ? "purple" : t1;
  const inputId = id || props.name;
  const inputRef = useRef(null);
  let t2;
  if ($[13] !== onChange) {
    t2 = () => {
      const input = inputRef.current;
      if (!input) {
        return;
      }
      input.value = "";
      onChange?.({
        target: input,
        currentTarget: input
      } as React.ChangeEvent<HTMLInputElement>);
    };
    $[13] = onChange;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  const clear = t2;
  let t3;
  if ($[15] !== color || $[16] !== inputId || $[17] !== label || $[18] !== props) {
    t3 = label && <Label htmlFor={inputId} color={color || "purple"} disabled={props.disabled} className="mx-5">{label}</Label>;
    $[15] = color;
    $[16] = inputId;
    $[17] = label;
    $[18] = props;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const t4 = error ? "error" : file ? "filled" : "empty";
  let t5;
  if ($[20] !== className || $[21] !== color || $[22] !== t4) {
    t5 = dropzoneVariants({
      color,
      state: t4,
      className
    });
    $[20] = className;
    $[21] = color;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== chooseLabel || $[25] !== clear || $[26] !== clearLabel || $[27] !== color || $[28] !== dragLabel || $[29] !== file || $[30] !== maxSizeLabel || $[31] !== props) {
    t6 = file ? <div className="flex items-center gap-sm"><Icon name="attachment" className="w-5 h-5 shrink-0" /><div className="min-w-0 flex-1"><p className="truncate text-sm">{file.name}</p><p className="text-xs opacity-70">{fileTypeLabel(file)}</p></div><button type="button" onClick={clear} disabled={props.disabled} aria-label={clearLabel} title={clearLabel} className="relative z-10 shrink-0 cursor-pointer rounded-full p-1 opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-2 disabled:cursor-not-allowed"><Icon name="close" className="w-4 h-4" /></button></div> : <div className="flex flex-col items-center gap-sm text-center"><p>{dragLabel}</p><p className="text-sm opacity-70">{maxSizeLabel}</p><Button isDummy={true} variant="primary" color={color === "dark" ? "yellow" : "purple"} label={chooseLabel} /></div>;
    $[24] = chooseLabel;
    $[25] = clear;
    $[26] = clearLabel;
    $[27] = color;
    $[28] = dragLabel;
    $[29] = file;
    $[30] = maxSizeLabel;
    $[31] = props;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  let t7;
  if ($[33] !== error || $[34] !== inputId) {
    t7 = error && <p id={inputId ? `${inputId}-error` : undefined} className="mt-md text-center text-sm text-red-500" role="alert">{error}</p>;
    $[33] = error;
    $[34] = inputId;
    $[35] = t7;
  } else {
    t7 = $[35];
  }
  const t8 = !!error;
  let t9;
  if ($[36] !== inputId || $[37] !== onChange || $[38] !== props || $[39] !== t8) {
    t9 = <input {...props} ref={inputRef} type="file" id={inputId} onChange={onChange} aria-invalid={t8} className="absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed" />;
    $[36] = inputId;
    $[37] = onChange;
    $[38] = props;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== t5 || $[42] !== t6 || $[43] !== t7 || $[44] !== t9) {
    t10 = <div className={t5}>{t6}{t7}{t9}</div>;
    $[41] = t5;
    $[42] = t6;
    $[43] = t7;
    $[44] = t9;
    $[45] = t10;
  } else {
    t10 = $[45];
  }
  let t11;
  if ($[46] !== t10 || $[47] !== t3) {
    t11 = <div className="flex flex-col gap-xs w-full">{t3}{t10}</div>;
    $[46] = t10;
    $[47] = t3;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  return t11;
};